/**
 * Subscription API Client
 *
 * This client provides subscription and quota data for the frontend-plugin-subscription.
 * Uses tRPC to communicate with the billing backend.
 */

import type {
  BillingInterval,
  CheckoutSessionResult,
  Plan,
  PublishedPlanDisplayResponse,
  QuotaStatus,
  Subscription,
  SubscriptionApiClient,
} from '@xhubio-saas/core-frontend/plugin-subscription'
import { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { trpc } from '@/trpc'

/**
 * Response type from tRPC billing.getSubscription
 */
interface SubscriptionResponse {
  id: string
  planId: string
  status: string
  currentPeriodStart: string
  currentPeriodEnd: string
  trialStart?: string
  trialEnd?: string
  cancelAt?: string
  canceledAt?: string
}

/**
 * Response type from tRPC billing.getQuotaStatus
 */
interface QuotaStatusResponse {
  eventType: string
  hasQuota: boolean
  limit?: number | 'unlimited'
  currentUsage: number
  remaining?: number
  periodType?: string
  periodStart?: string
  periodEnd?: string
}

/**
 * Creates a Subscription API client that uses tRPC to communicate with the backend.
 *
 * This function should be called within a React component that has access
 * to the tRPC context (inside TRPCProvider).
 *
 * @returns SubscriptionApiClient implementation using tRPC
 */
export function useSubscriptionClient(): SubscriptionApiClient {
  const utils = trpc.useUtils()
  const { i18n } = useTranslation()
  // 2-letter base code (e.g. "cs" from "cs-CZ") — matches billing translation languages.
  const language = (i18n.language || 'en').split('-')[0]

  // Create mutation for checkout session
  const checkoutMutation = trpc.billing.createCheckoutSession.useMutation()

  // Create mutation for portal session
  const portalMutation = trpc.billing.createPortalSession.useMutation()

  // Create mutation for downgrade to free
  const downgradeMutation = trpc.billing.downgradeToFree.useMutation()

  // Die Mutationen wandern in eine Ref: ihr Inhalt ist pro Render neu, die
  // Ref-Identitaet bleibt stabil. Gelesen wird `.current` NUR im asynchronen
  // Callback, nie waehrend des Renderns.
  const mutations = useRef({
    checkoutMutation,
    portalMutation,
    downgradeMutation,
  })
  mutations.current = {
    checkoutMutation,
    portalMutation,
    downgradeMutation,
  }

  return useMemo(
    () => ({
      async getSubscription(): Promise<Subscription | null> {
        // Invalidate cache to force fresh fetch
        await utils.billing.getSubscription.invalidate()

        const response =
          (await utils.billing.getSubscription.fetch()) as SubscriptionResponse | null

        if (!response) {
          return null
        }

        // Convert ISO strings back to Date objects
        return {
          id: response.id,
          planId: response.planId,
          status: response.status as Subscription['status'],
          currentPeriodStart: new Date(response.currentPeriodStart),
          currentPeriodEnd: new Date(response.currentPeriodEnd),
          trialStart: response.trialStart ? new Date(response.trialStart) : undefined,
          trialEnd: response.trialEnd ? new Date(response.trialEnd) : undefined,
          cancelAt: response.cancelAt ? new Date(response.cancelAt) : undefined,
          canceledAt: response.canceledAt ? new Date(response.canceledAt) : undefined,
        }
      },

      async getPlan(): Promise<Plan | null> {
        // Invalidate cache to force fresh fetch
        await utils.billing.getPlan.invalidate()
        return utils.billing.getPlan.fetch()
      },

      async getQuotaStatus(eventType: string): Promise<QuotaStatus | null> {
        // Invalidate cache to force fresh fetch
        await utils.billing.getQuotaStatus.invalidate({ eventType })
        const response = (await utils.billing.getQuotaStatus.fetch({
          eventType,
        })) as QuotaStatusResponse | null

        if (!response?.hasQuota) {
          return null
        }

        // Determine the limit value
        const limit = response.limit === 'unlimited' ? null : (response.limit ?? null)

        // Convert ISO strings back to Date objects
        return {
          eventType: response.eventType,
          currentUsage: response.currentUsage,
          limit,
          periodStart: response.periodStart ? new Date(response.periodStart) : new Date(),
          periodEnd: response.periodEnd ? new Date(response.periodEnd) : new Date(),
          period: (response.periodType as QuotaStatus['period']) ?? 'monthly',
        }
      },

      async createCheckoutSession(input: {
        planId: string
        billingPeriod?: BillingInterval
      }): Promise<CheckoutSessionResult> {
        const response = await mutations.current.checkoutMutation.mutateAsync({
          planId: input.planId,
          billingPeriod: (input.billingPeriod ?? 'monthly') as 'monthly' | 'yearly',
        })

        return {
          sessionId: response.sessionId,
          checkoutUrl: response.checkoutUrl,
          expiresAt: response.expiresAt ?? '',
        }
      },

      async downgradeToFree(): Promise<{ success: boolean; planId: string }> {
        return mutations.current.downgradeMutation.mutateAsync()
      },

      // Die Absage (z. B. billing-subscription-002) wird nicht geschluckt: sie reist
      // zur Seite weiter, gesprochen wird sie vom globalen Fehler-Toast
      // (CORE 2026-09-23/22 S4, F-2026-09-16-31).
      async getPortalUrl(): Promise<string | null> {
        // Zurück direkt auf die Billing-Seite — ohne returnUrl nimmt das Backend `/subscription`, das nur noch umleitet.
        const response = await mutations.current.portalMutation.mutateAsync({
          returnUrl: `${window.location.origin}/billing`,
        })
        return response.url ?? null
      },

      async getPricingData(): Promise<PublishedPlanDisplayResponse> {
        await utils.billing.getPublishedPlanDisplay.invalidate()
        const response = await utils.billing.getPublishedPlanDisplay.fetch({ language })

        return {
          plans: (response?.plans ?? []) as unknown as PublishedPlanDisplayResponse['plans'],
          features: (response?.features ??
            []) as unknown as PublishedPlanDisplayResponse['features'],
          quotaTypes: response?.quotaTypes ?? [],
          discounts: (response?.discounts ??
            []) as unknown as PublishedPlanDisplayResponse['discounts'],
        }
      },
    }),
    // NUR `utils` und `language` — letzteres ist ein String (Sprachcode), wird per
    // Wert verglichen und soll den Client bei Sprachwechsel bewusst neu bauen.
    // Keine Mutationen.
    [utils, language]
  )
}
